import React, { useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { JVDropdown } from "../../../shared/JVDropdown";
import { JVDropdownInput } from "../../../shared/JVDropdownInput";
import { JVInput } from "../../../shared/JVInput";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { IMainForm } from "./Dice";
import { states } from "../model";

export interface IFormFields {
  description?: string | null;
  query: string;
  country?: { name: string } | null;
  state?: { name: string } | null;
  //   city?: { name: string } | null;
  city?: { name: string }[] | null;
  timezone?: string[];
  relocation?: "Yes" | "No" | null;
  distance?: number;
}

const schema = yup.object({
  description: yup.string().nullable(),
  query: yup.string().required("Search query is required"),
  // country: yup.object({ name: yup.string().required("Country Name is required") }).required("Country is required"),
  state: yup.object().nullable(),
  //   city: yup.object().nullable(),
  city: yup
    .array()
    .of(yup.object({ name: yup.string() }))
    .nullable(),
  timezone: yup.array().of(yup.string()).nullable(),
  relocation: yup.string().nullable(),
  distance: yup.number().nullable(),
});

// Hardcoded timezone options
const timezoneOptions = [
  "Mountain Time Zone (US)",
  "Mountain Time Zone (Canada)",
  "Hawaii/Hawaii-Aleutian Time Zone",
  "Alaskan Time Zone",
  "Pacific Time Zone (US)",
  "Pacific Time Zone (Canada)",
  "Central Time Zone (US)",
  "Central Time Zone (Canada)",
  "Eastern Time Zone (US)",
  "Eastern Time Zone (Canada)",
  "Atlantic Time Zone (Canada)",
  "Newfoundland Time Zone (Canada)",
];

interface IProps {
  form1?: any;
  setForm1: (data: IFormFields) => void;
  setForm1Data: (data: any) => void;
  reload: boolean;
  setReload: (is: boolean) => void;
  loading: boolean;
  setLoading: (is: boolean) => void;
  toast: any;
}

export const Form = (props: IProps) => {
  const {
    control,
    handleSubmit,
    register,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      timezone: [],
      relocation: "No",
      country: { name: "United States" },
      distance: 0,
    },
  });

  const [countryOptions, setCountryOptions] = useState<{ name: string }[]>([]);
  const [stateOptions, setStateOptions] = useState<{ name: string }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ name: string }[]>([]);
  const [openHelp, setOpenHelp] = useState(false);
  const [selectedCities, setSelectedCities] = useState<{ name: string }[]>([]);
  const [citySearch, setCitySearch] = useState("");

  const selectedCountry = watch("country");
  const selectedState = watch("state");

  // ---------- Load Countries ----------
  const loadCountries = async (query: string) => {
    if (!query) return;
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?country=${query}&format=json&addressdetails=1&limit=10`);
      const data = await response.json();
      const countries = data
        .map((item: any) => item.address.country)
        .filter((v: string, i: number, a: string[]) => v && a.indexOf(v) === i)
        .map((name: string) => ({ name }));
      setCountryOptions(countries);
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };
  const completeMethodCountries = (event: { query: string }) => {
    loadCountries(event.query);
  };

  // ---------- Load States ----------
  const loadStates = async (query: string) => {
    if (!selectedCountry?.name) return;
    if (selectedCountry.name === "United States") {
      setStateOptions(states.filter((f) => f.name.toLowerCase().trim().includes(query.toLowerCase().trim())));
    } else {
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?country=${selectedCountry.name}&state=${query}&format=json&addressdetails=1&limit=10`);
        const data = await response.json();
        const states = data
          .map((item: any) => item.address.state)
          .filter((v: string, i: number, a: string[]) => v && a.indexOf(v) === i)
          .map((name: string) => ({ name }));
        setStateOptions(states);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    }
  };
  const completeMethodStates = (event: { query: string }) => {
    loadStates(event.query);
  };

  // ---------- Load Cities ----------
  //   const loadCities = async (query: string) => {
  //     if (!selectedCountry?.name || !selectedState?.name) {
  //       props?.toast?.current?.show({
  //         severity: "error",
  //         detail: "Select the Country and State first.",
  //         life: 3000,
  //       });
  //       return;
  //     }
  //     try {
  //       const response = await fetch(`https://nominatim.openstreetmap.org/search?city=${query}&state=${selectedState.name}&country=${selectedCountry.name}&format=json&addressdetails=1&limit=10`);
  //       const data = await response.json();
  //       const cities = data
  //         .map((item: any) => item.address.city || item.address.town || item.address.village)
  //         .filter((v: string, i: number, a: string[]) => v && a.indexOf(v) === i)
  //         .map((name: string) => ({ name }));
  //       setCityOptions(cities);
  //     } catch (err) {
  //       console.error("Error fetching cities:", err);
  //     }
  //   };

  const loadCities = async (query: string) => {
    if (!selectedState?.name) return;

    try {
      const response = await fetch("https://countriesnow.space/api/v0.1/countries/state/cities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          country: selectedCountry?.name ?? "United States",
          state: selectedState.name,
        }),
      });
      const data = await response.json();

      if (!data.error && data.data) {
        const filtered = (data.data as string[]).filter((city: string) => city.toLowerCase().includes(query.toLowerCase())).map((city: string) => ({ name: city }));
        setCityOptions(filtered);
      }
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const completeMethodCities = (event: { query: string }) => {
    loadCities(event.query);
  };

  useEffect(() => {
    setValue("state", null);
    setStateOptions([]);
    setValue("city", null);
    setCityOptions([]);
  }, [selectedCountry, setValue]);

  //   useEffect(() => {
  //     setValue("city", null);
  //     setSelectedCities([]);
  //     setCityOptions([]);
  //   }, [selectedState, setValue]);

  useEffect(() => {
    setValue("city", null);
    setSelectedCities([]);
    setCitySearch("");
    setCityOptions([]);
    if (selectedState?.name) {
      loadCities("");
    }
  }, [selectedState, setValue]);

  // ---------- Build Dice API payload ----------
  const buildPayload = (data: IFormFields) => {
    let locations: { value: string; distance?: number; distanceUnit?: string }[] = [];
    let newObj: any = {};
    if (data.country?.name) {
      newObj = { value: data.country.name };
    }
    if (data.state?.name) {
      newObj = { value: data.state.name };
    }
    // if (data.city?.name) {
    //   newObj = { value: data.city.name };
    // }
    if (newObj?.value !== undefined && newObj?.value !== null) {
      locations.push(newObj);
    }

    if (data.city && Array.isArray(data.city) && data.city.length > 0) {
      const locations2: { value: string; distance?: number; distanceUnit?: string }[] = [];
      if (data.country?.name && data.state?.name) {
        data.city.forEach((c) => {
          locations2.push({ value: `${c.name}, ${data.state?.name}, ${data.country?.name}` });
        });
      }
      else if (data.country?.name) {
        data.city.forEach((c) => {
          locations2.push({ value: `${c.name}, ${data.country?.name}` });
        });
      }
      else if (data.state?.name) {
        data.city.forEach((c) => {
          locations2.push({ value: `${c.name}, ${data.state?.name}` });
        });
      } else {
        data.city.forEach((c) => {
          locations2.push({ value: c.name });
        });
      }

      locations = locations2;
    }

    if (data?.distance !== null && data?.distance !== undefined && data?.distance > 0) {
      //newObj = { ...newObj, distance: data?.distance, distanceUnit: "miles" };
      locations = locations.map((el: { value: string; distance?: number; distanceUnit?: string }) => {
        return { ...el, distance: data?.distance, distanceUnit: "miles" };
      });
    }


    let formBody: IMainForm = {
      description: data.description !== undefined && data.description !== null && data.description.trim() !== "" ? data.description : null,
      q: data.query,
      locations: locations,
      //timezones: data.timezone,
      willingToRelocate: data?.relocation?.toLowerCase() === "yes",
    };

    if (formBody.description === null) delete formBody.description;

    return formBody;
  };

  const onSubmit = (data: IFormFields) => {
    const isCounrtyAvail: boolean = data?.country === undefined || data?.country === undefined || data?.country?.name === undefined || data?.country?.name === null;
    const isStateAvail: boolean = data?.state === undefined || data?.state === undefined || data?.state?.name === undefined || data?.state?.name === null;
    // const isCityAvail: boolean = data?.city === undefined || data?.city === undefined || data?.city?.name === undefined || data?.city?.name === null;
    const isCityAvail = selectedCities.length === 0;
    if (isCounrtyAvail === true && isStateAvail === true && isCityAvail === true) {
      props?.toast?.current?.show({ severity: "error", detail: `Country is required to search candidates`, life: 3000 });
    } else {
      const payload = buildPayload(data);

      if (payload.description !== null && payload.description !== undefined && payload.description.trim().length > 1) {
        if (payload.description.trim().length < 150) {
          props?.toast?.current?.show({
            severity: "error",
            detail: "Minimum 150 characters required",
            life: 3000,
          });
        } else {
          props.setForm1(data);
          props.setForm1Data(payload);
          props?.toast?.current?.show({
            severity: "success",
            detail: "Search Applied Successfully",
            life: 2000,
          });
        }
      } else {
        props.setForm1(data);
        props.setForm1Data(payload);
        props?.toast?.current?.show({
          severity: "success",
          detail: "Search Applied Successfully",
          life: 2000,
        });
      }
    }
    // props.setReload(!(props.reload));
  };

  const relocationOptions = [
    { label: "Willing to relocate anywhere", value: "Yes" },
    { label: "Not Willing to relocate anywhere", value: "No" },
  ];

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="border-top-3 border-primary ">
          <div className="p-fluid formgrid grid">
            <div className="field col-12">
              <textarea
                rows={6}
                className="p-inputtext p-component w-full"
                placeholder="Paste Job Description or Resume"
              // {...register("description")}
              />
              <small className="p-error">{errors?.description?.message}</small>
              <label className="font-semibold">Copy & Paste Job Description / Resume (min chars 150 )</label>
            </div>

            <div className="field col-12 md:col-12 flex">
              <div style={{ width: "98%", marginLeft: "-10px" }}>
                <JVInput column={1} label="Key Word or Boolean Search" control={control} name="query" error={errors?.query?.message} placeholder="Keyword or Boolean Search" />
              </div>
              <span
                style={{
                  borderRadius: "50%",
                  fontSize: "1.5rem",
                  backgroundColor: "#2196F3",
                  padding: "5px",
                  color: "white",
                  height: "31px",
                  marginTop: "1.75rem",
                }}
                className="pi pi-question-circle"
                onClick={() => setOpenHelp(true)}
              />
            </div>

            {/* Country Dropdown */}
            <JVDropdownInput column={6} required completeMethod={completeMethodCountries} label="Country" control={control} name="country" options={countryOptions} optionLabel="name" placeholder="Search Country" error={errors?.country?.message} />

            {/* State Dropdown */}
            <JVDropdownInput column={6} completeMethod={completeMethodStates} label="State" control={control} name="state" options={stateOptions} optionLabel="name" placeholder="Search State" error={errors?.state?.message} disabled={!selectedCountry?.name} />

            {/* City Dropdown */}
            {/* <JVDropdownInput
                            column={6}
                            completeMethod={completeMethodCities}
                            label="City"
                            control={control}
                            name="city"
                            options={cityOptions}
                            optionLabel="name"
                            placeholder="Search City"
                            error={errors?.city?.message}
                            disabled={!selectedState?.name}
                        /> */}

            {/* City  */}

            <div className="field col-12 md:col-3">
              <p>City</p>
              <MultiSelect
                style={{ height: "37px", marginTop: "-10px" }}
                value={selectedCities}
                onChange={(e) => {
                  setSelectedCities(e.value);
                  setValue("city", e.value);
                }}
                options={cityOptions}
                optionLabel="name"
                display="chip"
                filter
                onFilter={(e) => setCitySearch(e.filter)}
                onHide={() => setCitySearch("")}
                placeholder="Select Cities"
                disabled={!selectedState?.name}
                virtualScrollerOptions={{ itemSize: 43 }}
                className="w-full"
              />
              {errors?.city && <small className="p-error">{errors?.city?.message}</small>}
            </div>
            {/* Distance */}
            <JVInput column={6} label="Distance In Miles" control={control} name="distance" error={errors?.distance?.message} placeholder="Enter Numbers for Distance in Miles" />

            {/* Timezone */}
            {/* <div className="field col-12 md:col-6">
                            <label htmlFor="Time Zone">Time Zone</label>
                            <Controller
                                name="timezone"
                                control={control}
                                render={({ field }) => (
                                    <MultiSelect
                                        options={timezoneOptions}
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.value)}
                                        multiple
                                        placeholder="Select one or more timezones"
                                        className={errors?.timezone ? "p-invalid" : ""}
                                    />
                                )}
                            />
                            {errors?.timezone && (
                                <small className="p-error">{errors?.timezone?.message}</small>
                            )}
                        </div> */}

            {/* Relocation */}
            <JVDropdown column={6} label="Willing To Relocate" control={control} name="relocation" options={relocationOptions} optionLabel="label" optionValue="value" placeholder="Select" />
            {errors?.relocation && <small className="p-error">{errors?.relocation?.message}</small>}

            <div className="col-12 mt-3">
              <div className="grid">
                <div className="col-6">
                  <Button loading={props.loading} disabled={props.loading} className="w-full" label="Search" type="submit" />
                </div>
                <div className="col-6">
                  <Button
                    loading={props.loading}
                    disabled={props.loading}
                    className="w-full p-button-outlined"
                    label="Clear"
                    type="button"
                    onClick={() => {
                      reset({
                        query: "",
                        timezone: [],
                        relocation: null,
                        distance: 0,
                      });
                      props.setForm1Data({ q: "" });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </form>

      <Dialog visible={openHelp} style={{ width: "80vw" }} header="How To Search" modal className="p-fluid" onHide={() => setOpenHelp(false)}>
        <div style={{ fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
          <h2 style={{ color: "green" }}>✅ How to Use the Search Bar (Frontend)</h2>
          <p>You can search using:</p>
          <ul>
            <li>
              📘 <strong>Single keywords</strong> (e.g., <code>Java</code>)
            </li>
            <li>
              🔗 <strong>Multiple comma-separated terms</strong> (e.g., <code>Java, Spring, Developer</code>)
            </li>
            <li>
              ✖️ <strong>Quoted phrases</strong> (e.g., <code>"full stack developer"</code>)
            </li>
            <li>
              ⚙️ <strong>Logical operators</strong> like <code>AND</code>, <code>OR</code>, <code>NOT</code>
            </li>
          </ul>
        </div>
      </Dialog>
    </>
  );
};
